export const SHARE_LOCATION_GESTURE_REJECT_MS = 500;
export const SHARE_LOCATION_QUIET_RETRY_MS = 12_000;
export const SHARE_LOCATION_LOW_ACCURACY_METERS = 100;
export const SHARE_LOCATION_TIMEOUT_MS = 10_000;

export const SHARE_LOCATION_POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: SHARE_LOCATION_TIMEOUT_MS,
};

export const SHARE_LOCATION_COPY = {
  product: 'Text a Photo',
  loading: 'Loading',
  sharing: 'Sharing your location',
  askingHeading: 'Share where this is',
  askingBody: 'Your phone will ask to share your location.',
  askingFallback: 'Not there anymore? Reply to the text with the street address.',
  shareButton: 'Share my location',
  receivedHeading: 'Location received',
  receivedLabel: 'Added to your report',
  couldntGetHeading: "We couldn't get your location",
  couldntGetBody: 'Reply to the text with the street address instead.',
  tryAgain: 'Try again',
  couldntSaveHeading: "We couldn't save that location",
  couldntSaveBody: 'Your report was still sent. You can close this page.',
  outsideBody: "We've texted you a new link.",
  expiredHeading: 'This link has expired',
  expiredBody: '',
  backToMessages: 'Back to Messages',
} as const;

const SMS_E164 = /^\+[1-9]\d{1,14}$/;

export type GeolocationPermissionState = 'granted' | 'prompt' | 'denied' | 'unknown';

export function geolocationDenialNeedsTap(input: {
  fromUserGesture: boolean;
  permissionState: GeolocationPermissionState;
  elapsedMs: number;
}): boolean {
  if (input.fromUserGesture) {
    return false;
  }
  if (input.permissionState === 'denied' || input.permissionState === 'granted') {
    return false;
  }
  if (input.permissionState === 'prompt') {
    return true;
  }

  return input.elapsedMs < SHARE_LOCATION_GESTURE_REJECT_MS;
}

export function nextQuietWait(elapsedMs: number, budgetMs: number, retryAfterSeconds: number): number | null {
  if (elapsedMs >= budgetMs || !Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
    return null;
  }
  const wait = Math.min(retryAfterSeconds * 1000, budgetMs - elapsedMs);

  return wait > 0 ? wait : null;
}

export function shareLocationSmsHref(textNumber?: string | null): string | null {
  const value = (textNumber || '').trim();
  if (!SMS_E164.test(value)) {
    return null;
  }

  return `sms:${value}`;
}

export function shareLocationOutsideHeading(agencyName?: string | null): string {
  const agency = (agencyName || '').trim() || 'the agency';

  return `That spot is outside ${agency}`;
}

export function receivedAccessibleName(matchedAddress: string): string {
  return `${SHARE_LOCATION_COPY.receivedLabel}: ${matchedAddress}`;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export type SharePositionResult =
  | { ok: true; lat: number; long: number; accuracyM: number }
  | { ok: false; reason: 'unavailable' | 'needs-tap' | 'inaccurate' };

async function readGeolocationPermission(): Promise<GeolocationPermissionState> {
  const permissions = navigator.permissions;
  if (!permissions?.query) {
    return 'unknown';
  }
  try {
    const status = await permissions.query({ name: 'geolocation' as PermissionName });
    if (status.state === 'granted' || status.state === 'prompt' || status.state === 'denied') {
      return status.state;
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

function getPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, SHARE_LOCATION_POSITION_OPTIONS);
  });
}

export async function readSharePosition(fromUserGesture: boolean): Promise<SharePositionResult> {
  const before = await readGeolocationPermission();
  if (before === 'denied' || !navigator.geolocation) {
    return { ok: false, reason: 'unavailable' };
  }
  const started = Date.now();
  try {
    const position = await getPosition();
    const { latitude, longitude, accuracy } = position.coords;
    if (!Number.isFinite(accuracy) || accuracy > SHARE_LOCATION_LOW_ACCURACY_METERS) {
      return { ok: false, reason: 'inaccurate' };
    }

    return { ok: true, lat: latitude, long: longitude, accuracyM: accuracy };
  } catch (error) {
    const code = (error as { code?: number }).code;
    if (code === 1) {
      const after = await readGeolocationPermission();
      if (geolocationDenialNeedsTap({ fromUserGesture, permissionState: after, elapsedMs: Date.now() - started })) {
        return { ok: false, reason: 'needs-tap' };
      }
    }

    return { ok: false, reason: 'unavailable' };
  }
}
